<template>
  <div class="row">
    <div class="col-lg-7">
      <div class="ibox">
        <div class="ibox-title">
          <h1>
            <span v-if="existingDocumentId">Edit</span><span v-else>New</span>
            {{ formTitle }}
          </h1>
        </div>
        <div class="ibox-content">
          <div role="form">
            <div class="form-group">
              <label for="document_name" class="text-14 font-semi text-gray-700">Document Title</label>
              <input id="document_name" v-model="name" type="text" class="form-control max-w-xl" placeholder="e.g. 2026 Season Liability Waiver">
            </div>
            <div class="form-group">
              <label class="text-14 font-semi text-gray-700 m-b-xs">What kind of document is this?</label>
              <div class="flex align-items-start m-b-sm">
                <input type="radio" id="dockind_liability" value="liability" v-model="docKind">
                <div class="m-l-sm">
                  <label for="dockind_liability" class="m-b-none">Liability form</label>
                  <p class="text-gray-600 m-b-none">Parents sign once per wrestler at registration.</p>
                </div>
              </div>
              <div class="flex align-items-start m-b-sm">
                <input type="radio" id="dockind_signature" value="signature" v-model="docKind">
                <div class="m-l-sm">
                  <label for="dockind_signature" class="m-b-none">Required signature</label>
                  <p class="text-gray-600 m-b-none">Each coach, parent, or wrestler signs for themselves &mdash; e.g. code of conduct or concussion form. Users cannot use WrestlingIQ until they've signed.</p>
                </div>
              </div>
              <div class="flex align-items-start">
                <input type="radio" id="dockind_reference" value="reference" v-model="docKind">
                <div class="m-l-sm">
                  <label for="dockind_reference" class="m-b-none">Reference document</label>
                  <p class="text-gray-600 m-b-none">No signature needed &mdash; handouts, rules, schedules, etc.</p>
                </div>
              </div>
              <p class="text-14 m-t-sm m-b-none">
                <a
                  href="https://help.wrestlingiq.com/article/51-whats-the-difference-between-a-liability-form-and-a-signature-required-form"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Compare liability vs. required signature&nbsp;&nbsp;<i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                </a>
              </p>
            </div>
            <div v-if="docKind !== 'liability'" class="form-group">
              <label class="text-14 font-semi text-gray-700 m-b-xs">Visibility</label>
              <div class="flex align-items-start m-b-sm">
                <input type="radio" id="visibility_everyone" :value="false" v-model="coachesOnly">
                <div class="m-l-sm">
                  <label for="visibility_everyone" class="m-b-none">Everyone on the team</label>
                </div>
              </div>
              <div class="flex align-items-start">
                <input type="radio" id="visibility_coaches" :value="true" v-model="coachesOnly">
                <div class="m-l-sm">
                  <label for="visibility_coaches" class="m-b-none">Coaches only</label>
                  <p class="text-gray-600 m-b-none">Hide this document from parents and wrestlers.</p>
                </div>
              </div>
            </div>
            <div v-if="docKind !== 'reference'" class="form-group">
              <label class="text-14 font-semi text-gray-700 m-b-xs">How often is this signed?</label>
              <div class="flex align-items-start m-b-sm">
                <input type="radio" id="signfreq_once" value="once" v-model="signingFrequency">
                <div class="m-l-sm">
                  <label for="signfreq_once" class="m-b-none">Sign once</label>
                  <p class="text-gray-600 m-b-none">Speeds up checkout for returning users. You can still trigger a re-signing at any time from the documents list.</p>
                </div>
              </div>
              <div class="flex align-items-start">
                <input type="radio" id="signfreq_every" value="every_registration" v-model="signingFrequency">
                <div class="m-l-sm">
                  <label for="signfreq_every" class="m-b-none">Sign every registration</label>
                  <p class="text-gray-600 m-b-none">Users must re-sign on every registration, even if they've signed before.</p>
                </div>
              </div>
            </div>
            <div v-if="docKind !== 'reference'" class="form-group">
              <label class="text-14 font-semi text-gray-700 m-b-xs">When should this document be used?</label>
              <div class="flex align-items-start m-b-sm">
                <input type="radio" id="autoattach_all" :value="true" v-model="autoAttachToSessions">
                <div class="m-l-sm">
                  <label for="autoattach_all" class="m-b-none">Use on every new registration</label>
                  <p class="text-gray-600 m-b-none">Signed by everyone registering, unless a registration is set up to use specific documents only.</p>
                </div>
              </div>
              <div class="flex align-items-start">
                <input type="radio" id="autoattach_specific" :value="false" v-model="autoAttachToSessions">
                <div class="m-l-sm">
                  <label for="autoattach_specific" class="m-b-none">Only use on specific registrations</label>
                  <p class="text-gray-600 m-b-none">Stays out of every registration by default. Attach it from the Documents &amp; waivers section in each registration.</p>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label for="x-editor" class="text-14 font-semi text-gray-700">Content</label>
              <input id="x-editor" type="hidden" name="content">
              <trix-editor input="x-editor" class="trix-content"></trix-editor>
            </div>
            <div class="button-area text-right">
              <div v-show="error">
                <small>
                  <span class="text-danger">{{ errorMessage }}</span>
                </small>
              </div>
              <ladda-button @lbClicked="pingServer" el-class="btn-primary w-full-xs-only" :loading="loading">
                {{ buttonText() }}
              </ladda-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-lg-5">
      <div class="ibox">
        <div class="ibox-content">
          <div class="p-t-m">
            <div class="relative border border-solid border-green-200 bg-green-50 rounded-xxs">
              <div class="flex align-items-center gap-xs p-x-m p-y-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-green-700">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                </svg>
                <span class="text-12 font-heavy uppercase tracking-wide text-green-700">Tip</span>
              </div>
              <div class="p-x-m p-b-m">
                <p class="font-semi m-b-xs">If you already have a <span v-if="docKind === 'liability'">liability form</span><span v-else>document</span>, copy and paste the text into the WrestlingIQ document editor.</p>
                <p class="text-14 text-gray-600 m-b-none">Adding your file as an attachment won't show the content to users<span v-if="docKind !== 'reference'"> signing the form</span>. See the document preview below.</p>
              </div>
            </div>
          </div>
          <div id="preview-frame" class="app-frame mac scrolling" data-title="Document Preview | Mobile Browser"
               style="max-width: 480px; height: 510px; margin-left: auto; margin-right: auto;">
            <div class="agreement-signature">
              <div class="ibox agreements">
                <div class="ibox-title">
                  <h1 v-if="name">{{ name }}</h1>
                  <h1 v-else>Document Title</h1>
                </div>
                <div class="ibox-content">
                  <template>
                    <div class="trix-content">
                      <div v-html="content"></div>
                      <div v-if="!content">
                        <p class="text-gray-600">No content to preview, please add content to the text editor to preview.</p>
                      </div>
                    </div>
                    <div v-if="docKind !== 'reference'">
                      <div class="hr-line-dashed"></div>
                      <div class="form-group m-t-lg agree-box">
                        <div class="pull-left">
                          <div v-if="docKind === 'liability'">
                            <strong>Wrestler: </strong> [Wrestler name here]
                          </div>
                          <div>
                            <strong>Signed: </strong> [Parent/guardian name here]
                          </div>
                          <div>
                            <strong>Date: </strong> {{ today() }}
                          </div>
                        </div>
                        <div class="clearfix"></div>
                        <div class="m-t-sm">
                          <div>
                            <input type="checkbox" id="agree_checkbox" data-cy="agreement-check"> I agree
                          </div>
                        </div>
                        <div class="m-t-sm">
                          <button name="button" class="btn btn-primary" data-cy="submit">
                            Next
                          </button>
                        </div>
                        <p class="bg-gray-50 text-10 m-t-m p-y-sm p-x-xs">
                          <i class="fa-solid fa-circle-info m-r-xs"></i>The next button takes people to the signature screen when not in demo mode.
                        </p>
                        <div class="clearfix"></div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { trixFileAttachmentMix } from './mixins/trix_file_attachment_mix';

export default {
  name: 'document-form',
  components: {
    LaddaButton,
  },
  mixins: [trixFileAttachmentMix],
  props: {
    docType: {
      type: String,
      default: null,
    },
    existingDocumentId: {
      type: Number,
      default: null,
    },
    existingDocumentName: {
      type: String,
      default: null,
    },
    existingDocumentContent: {
      type: String,
      default: null,
    },
    existingDocumentNeedsSig: {
      type: Boolean,
      default: false,
    },
    existingDocumentIsLiability: {
      type: Boolean,
      default: false,
    },
    existingDocumentCoachesOnly: {
      type: Boolean,
      default: false,
    },
    existingDocumentSigningFrequency: {
      type: String,
      default: null,
    },
    existingDocumentAutoAttach: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: 'Error, please try again later or contact support.',

      name: '',
      docId: null,
      content: '',
      docKind: 'reference',
      coachesOnly: false,
      signingFrequency: 'once',
      autoAttachToSessions: true,
    };
  },
  computed: {
    formTitle() {
      if (this.docKind === 'liability') return 'Liability Form';
      if (this.docKind === 'signature') return 'Signed Document';
      return 'Reference Document';
    },
  },
  watch: {
    docKind(newKind) {
      if (newKind === 'liability') {
        this.coachesOnly = false;
      }
    },
  },
  mounted() {
    const vm = this;

    if (vm.existingDocumentName) {
      vm.name = vm.existingDocumentName;
    }

    if (vm.existingDocumentId) {
      vm.docId = vm.existingDocumentId;
    }

    if (vm.existingDocumentIsLiability || vm.docType === 'liability') {
      vm.docKind = 'liability';
    } else if (vm.existingDocumentNeedsSig || vm.docType === 'signature') {
      vm.docKind = 'signature';
    } else {
      vm.docKind = 'reference';
    }

    if (vm.docKind !== 'liability' && vm.existingDocumentCoachesOnly) {
      vm.coachesOnly = true;
    }

    if (vm.existingDocumentSigningFrequency) {
      vm.signingFrequency = vm.existingDocumentSigningFrequency;
    }

    vm.autoAttachToSessions = vm.existingDocumentAutoAttach;

    document.addEventListener('trix-initialize', vm.trixInitHandler);
    document.addEventListener('trix-change', vm.trixChangeHandler);
  },
  destroyed() {
    document.removeEventListener('trix-initialize', this.trixInitHandler);
    document.removeEventListener('trix-change', this.trixChangeHandler);
  },
  methods: {
    today() {
      return moment().format('M/D/YYYY');
    },
    trixInitHandler(event) {
      if (this.existingDocumentContent) {
        this.content = this.existingDocumentContent;
        const { editor } = event.target;
        editor.loadHTML(this.content);
      }
    },
    trixChangeHandler(event) {
      const vm = this;
      vm.content = event.target.value;
    },
    validateNotEmpty(name, content) {
      const vm = this;
      let errorMessage = 'Please ensure ';
      let error = false;
      if (name.length === 0) {
        errorMessage += 'name, ';
        error = true;
      }
      if (content.length === 0) {
        errorMessage += 'content, ';
        error = true;
      }
      if (error) {
        errorMessage = errorMessage.slice(0, -2);
        errorMessage += ' is not empty.';
        vm.errorMessage = errorMessage;
        vm.error = true;
        return false;
      }
      return true;
    },
    buttonText() {
      if (this.docId != null) {
        return `Update ${this.formTitle}`;
      }
      return `Create ${this.formTitle}`;
    },
    successHandler(name, id) {
      const vm = this;
      vm.loading = false;
      vm.$notificationManager.emit('show-toast', `${name} saved`, true);
      window.location.assign(`/documents?recently_saved=${id}`);
    },
    errorHandler() {
      const vm = this;
      vm.loading = false;
      vm.errorMessage = 'Error, please try again later or contact support.';
      vm.error = true;
    },
    pingServer: _.throttle(function () {
      const vm = this;

      if (vm.loading === true) {
        return;
      }

      const { name } = vm;
      const content = $('#x-editor').val();

      if (vm.validateNotEmpty(name, content)) {
        vm.loading = true;

        const params = {
          document: {
            name,
            needs_signature: vm.docKind !== 'reference',
            liability_form: vm.docKind === 'liability',
            coaches_only: vm.docKind === 'liability' ? false : vm.coachesOnly,
            signing_frequency: vm.signingFrequency,
            auto_attach_to_sessions: vm.docKind === 'reference' ? true : vm.autoAttachToSessions,
            document_versions_attributes: [
              {
                content,
              },
            ],
          },
        };

        if (vm.docId != null) {
          // PUT
          const url = vm.$apiService.updateDocumentUrl(vm.docId);
          axios.put(url, params)
            .then((response) => {
              vm.successHandler(name, vm.docId);
            })
            .catch((error) => {
              vm.errorHandler();
            });
        } else {
          // POST
          const url = vm.$apiService.documentsUrl();
          axios.post(url, params)
            .then((response) => {
              const { data } = response;
              vm.successHandler(data.name, data.id);
            })
            .catch((error) => {
              vm.errorHandler();
            });
        }
      }
    }, 500),
  },
};
</script>
